import React, { useContext, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import {
    Box,
    Flex,
    Button,
    Text,
    Link,
    VStack,
    Divider,
    Icon,
    HStack,
    useBreakpointValue,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter
} from "@chakra-ui/react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { featureRoutes, FEATURE_IDS } from "../constants/featureCostants";
import {
    FaUtensils,
    FaUsers,
    FaTicketAlt,
    FaSyncAlt,
    FaRobot,
    FaBriefcase,
    FaMapMarkerAlt,
    FaSignOutAlt,
    FaCalendarTimes,
    FaLink,
    FaFileInvoiceDollar,
} from "react-icons/fa";

const FEATURE_ICONS = {
    [FEATURE_IDS.ONLINE_MENU]: FaUtensils,
    [FEATURE_IDS.TICKETS]: FaTicketAlt,
    [FEATURE_IDS.RECURRING_TICKETS]: FaSyncAlt,
    [FEATURE_IDS.USERS]: FaUsers,
    [FEATURE_IDS.CHAT]: FaRobot,
    [FEATURE_IDS.LINKS]: FaLink,
    [FEATURE_IDS.CAREERS]: FaBriefcase,
    [FEATURE_IDS.EXPIRATION_CHECK]: FaCalendarTimes,
    [FEATURE_IDS.PRICE_LISTS]: FaFileInvoiceDollar,
};

const Navbar = () => {
    const { user, logout, currLocation, locations, features } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);

    const locationName = locations.find(item => item.id === currLocation)?.name || '';

    const handleLogoutClose = () => {
        setLogoutModalOpen(false);
    };

    const handleLogout = () => {
        setLogoutModalOpen(false);
        logout();
        navigate("/login");
    };

    if (!user || location.pathname === "/login") return null;
    if (location.pathname === "/choose-location" && !currLocation) return null;

    const visibleFeatures = featureRoutes.filter((feature) => features.includes(feature.id));

    if (isMobile) {
        return (
            <>
                <Box
                    w="100vw"
                    maxWidth="100vw"
                    bg="white"
                    color="brand.700"
                    boxShadow="soft"
                    borderBottom="1px solid"
                    borderColor="gray.100"
                    px={4}
                    py={3}
                    position="fixed"
                    top="0"
                    zIndex="1000"
                    overflow="hidden"
                    h="auto"
                >
                    <Flex align="center" gap={2} mb={3}>
                        <Flex
                            align="center"
                            justify="center"
                            boxSize="30px"
                            borderRadius="lg"
                            bg="brand.50"
                            color="brand.600"
                            flexShrink={0}
                        >
                            <Icon as={FaMapMarkerAlt} boxSize={3.5} />
                        </Flex>
                        <Text fontSize="sm" fontWeight="700" noOfLines={1}>{locationName}</Text>
                    </Flex>

                    <Box
                        w="full"
                        overflowX="auto"
                        css={{
                            "&::-webkit-scrollbar": { display: "none" },
                            "msOverflowStyle": "none",
                            "scrollbarWidth": "none",
                        }}
                    >
                        <HStack
                            w="full"
                            minWidth="100%"
                            spacing={2}
                            align="center"
                            whiteSpace="nowrap"
                            display="flex"
                            flexWrap="nowrap"
                        >
                            {visibleFeatures.map((feature) => {
                                const isActive = location.pathname === feature.link;
                                return (
                                    <Link
                                        key={feature.id}
                                        as={RouterLink}
                                        to={feature.link}
                                        px={4}
                                        py={2}
                                        borderRadius="full"
                                        fontSize="sm"
                                        fontWeight="600"
                                        bg={isActive ? "brand.600" : "surface.100"}
                                        color={isActive ? "white" : "brand.700"}
                                        _hover={{ bg: isActive ? "brand.700" : "brand.50" }}
                                        transition="all 0.15s ease-out"
                                        display="flex"
                                        alignItems="center"
                                        gap={2}
                                    >
                                        <Icon as={FEATURE_ICONS[feature.id] || FaTicketAlt} boxSize={3.5} />
                                        {feature.name}
                                    </Link>
                                );
                            })}
                            {locations?.length > 1 && (
                                <Link
                                    as={RouterLink}
                                    to="/choose-location"
                                    px={4}
                                    py={2}
                                    borderRadius="full"
                                    fontSize="sm"
                                    fontWeight="600"
                                    color={location.pathname === "/choose-location" ? "white" : "brand.700"}
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                    bg={location.pathname === "/choose-location" ? "brand.600" : "surface.100"}
                                    _hover={{ bg: "brand.50" }}
                                    transition="all 0.15s ease-out"
                                    flexShrink={0}
                                >
                                    <Icon as={FaMapMarkerAlt} boxSize={3.5} />
                                    Odabir lokacije
                                </Link>
                            )}
                        </HStack>
                    </Box>
                </Box>
                <Box mt="104px"></Box>

                <LogoutModal isOpen={isLogoutModalOpen} onClose={handleLogoutClose} onConfirm={handleLogout} />
            </>
        );
    }

    return (
        <Box
            w="228px"
            h="calc(100vh - 20px)"
            bg="white"
            color="brand.700"
            borderRadius="2xl"
            boxShadow="card"
            border="1px solid"
            borderColor="gray.100"
            p={5}
            position="fixed"
            left="10px"
            top="10px"
            overflow="hidden"
            display="flex"
            flexDirection="column"
        >
            <Flex align="center" gap={3} mb={5} px={1}>
                <Flex
                    align="center"
                    justify="center"
                    boxSize="38px"
                    borderRadius="xl"
                    bg="brand.50"
                    color="brand.600"
                    flexShrink={0}
                >
                    <Icon as={FaMapMarkerAlt} boxSize={4} />
                </Flex>
                <Box minW={0}>
                    <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.06em" color="gray.400" fontWeight="700">
                        Lokacija
                    </Text>
                    <Text fontSize="sm" fontWeight="700" color="brand.700" whiteSpace="normal" wordBreak="break-word">
                        {locationName}
                    </Text>
                </Box>
            </Flex>

            <Divider borderColor="gray.100" mb={3} />

            <VStack align="stretch" spacing={1} w="full" flex="1" overflowY="auto">
                {visibleFeatures.map((feature) => {
                    const isActive = location.pathname === feature.link;
                    return (
                        <Link
                            key={feature.id}
                            as={RouterLink}
                            to={feature.link}
                            position="relative"
                            w="full"
                            display="flex"
                            alignItems="center"
                            gap={3}
                            pl={4}
                            pr={3}
                            py={2.5}
                            borderRadius="lg"
                            fontSize="sm"
                            fontWeight="600"
                            color={isActive ? "brand.700" : "gray.500"}
                            bg={isActive ? "brand.50" : "transparent"}
                            _hover={{ bg: isActive ? "brand.50" : "gray.50", color: "brand.700" }}
                            transition="all 0.15s ease-out"
                        >
                            {isActive && (
                                <Box
                                    position="absolute"
                                    left="0"
                                    top="22%"
                                    bottom="22%"
                                    w="3px"
                                    borderRadius="full"
                                    bg="brand.600"
                                />
                            )}
                            <Icon as={FEATURE_ICONS[feature.id] || FaTicketAlt} boxSize={4} color={isActive ? "brand.600" : "gray.400"} />
                            {feature.name}
                        </Link>
                    );
                })}

                {locations?.length > 1 && (
                    <Link
                        as={RouterLink}
                        to="/choose-location"
                        w="full"
                        display="flex"
                        alignItems="center"
                        gap={3}
                        pl={4}
                        pr={3}
                        py={2.5}
                        borderRadius="lg"
                        fontSize="sm"
                        fontWeight="600"
                        color={location.pathname === "/choose-location" ? "brand.700" : "gray.500"}
                        bg={location.pathname === "/choose-location" ? "brand.50" : "transparent"}
                        _hover={{ bg: "gray.50", color: "brand.700" }}
                        transition="all 0.15s ease-out"
                    >
                        <Icon as={FaMapMarkerAlt} boxSize={4} color="gray.400" />
                        Odabir lokacije
                    </Link>
                )}
            </VStack>

            <Divider borderColor="gray.100" my={3} />

            <Button
                variant="ghost"
                w="full"
                justifyContent="flex-start"
                gap={3}
                pl={4}
                fontSize="sm"
                color="red.500"
                fontWeight="600"
                borderRadius="lg"
                _hover={{ bg: "red.50" }}
                onClick={() => setLogoutModalOpen(true)}
            >
                <Icon as={FaSignOutAlt} boxSize={4} />
                Odjava
            </Button>

            <LogoutModal isOpen={isLogoutModalOpen} onClose={handleLogoutClose} onConfirm={handleLogout} />
        </Box>
    );
};

const LogoutModal = ({ isOpen, onClose, onConfirm }) => (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent mx={4}>
            <ModalHeader color="brand.700">Odjava</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
                <Text color="gray.600">Želite li se odjaviti?</Text>
            </ModalBody>
            <ModalFooter gap={3}>
                <Button variant="ghost" onClick={onClose}>Povratak</Button>
                <Button colorScheme="red" onClick={onConfirm}>Odjava</Button>
            </ModalFooter>
        </ModalContent>
    </Modal>
);

export default Navbar;
